import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui';
import { designTokens } from '../utils/designTokens';

const DocumentationSection = ({ title, children, id }) => (
  <section id={id} className="mb-12 scroll-mt-20">
    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">{title}</h2>
    {children}
  </section>
);

const GuideCard = ({ icon, title, description, steps }) => (
  <Card>
    <div className={`p-${designTokens.spacing.cardPadding}`}>
      <div className="flex items-center mb-4">
        <span className="text-3xl mr-3">{icon}</span>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{description}</p>
      <ol className="space-y-2">
        {steps.map((step, index) => (
          <li key={index} className="flex items-start">
            <span className="text-purple-600 dark:text-purple-400 font-bold mr-3">{index + 1}.</span>
            <span className="text-gray-700 dark:text-gray-300">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  </Card>
);

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const tableOfContents = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'for-creators', label: 'For Creators' },
    { id: 'for-sellers', label: 'For Sellers' },
    { id: 'earning-coins', label: 'Earning Boop Coins' },
    { id: 'premium-features', label: 'Premium Features' },
    { id: 'best-practices', label: 'Best Practices' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>Documentation - Boop</title>
        <meta name="description" content="Complete guide to using Boop: getting started, creating prompts, earning coins, using premium features, and selling in the marketplace." />
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 text-white">
        <div className={`max-w-7xl mx-auto px-${designTokens.spacing.pagePadding} py-12`}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Documentation</h1>
          <p className="text-xl text-purple-100">
            Everything you need to master Boop
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-8">
        {/* Sidebar - Table of Contents */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="sticky top-20">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Contents</h2>
            <nav className="space-y-2">
              {tableOfContents.map(item => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setActiveSection(item.id)}
                  className={`block py-2 px-4 rounded-lg transition-colors ${
                    activeSection === item.id
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100 font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl">

          {/* Getting Started */}
          <DocumentationSection title="Getting Started" id="getting-started">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <GuideCard
                icon="👤"
                title="For New Users"
                description="Start your Boop journey in 5 simple steps"
                steps={[
                  'Create a free account with email or Google',
                  'Browse prompts in categories like Creative Writing, Coding, Business',
                  'Try a prompt - copy and test in ChatGPT, Claude, or your favorite AI',
                  'Rate & Review - share what worked (earn 2 BC per detailed rating)',
                  'Create Your First - share your best prompt (earn 10 BC bonus)'
                ]}
              />

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Quick Tips</h3>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span>New users get 50 free Boop Coins to try premium features</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span>You can view 1 full prompt before signing up</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span>Core features are free forever - no credit card required</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span>Level up automatically as you contribute</span>
                    </li>
                  </ul>
                </div>
              </Card>
            </div>
          </DocumentationSection>

          {/* For Creators */}
          <DocumentationSection title="For Creators" id="for-creators">
            <GuideCard
              icon="✍️"
              title="Creating Great Prompts"
              description="Make your prompts discoverable and useful"
              steps={[
                'Write Clear Titles - Describe what the prompt does in plain language',
                'Add Examples - Show input/output to give context',
                'Tag Properly - Use relevant tags to help users find your work',
                'Engage Comments - Answer questions and build your reputation',
                'Iterate Over Time - Fork your own prompts to improve them'
              ]}
            />

            <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Prompt Quality Checklist</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <span>Clear, specific objective (e.g., "Write a...", "Analyze...", "Generate...")</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <span>Context and constraints clearly stated</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <span>Examples provided when helpful</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <span>Tone, style, and format specified</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <span>Tested before publishing</span>
                </li>
              </ul>
            </div>
          </DocumentationSection>

          {/* For Sellers */}
          <DocumentationSection title="For Sellers" id="for-sellers">
            <GuideCard
              icon="💰"
              title="Selling in the Marketplace"
              description="Monetize your expertise with these steps"
              steps={[
                'Build Reputation - Reach Level A (10+ free prompts, 25+ ratings given)',
                'Maintain Quality - Keep 7.0+ average rating on your free content',
                'Apply for Selling - Auto-approved when you meet all requirements',
                'Price Fairly - Recommend 20-50 BC for most content (max 200 BC)',
                'Provide Support - Answer buyer questions and maintain quality'
              ]}
            />

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Seller Requirements</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                    <li>✓ Account age: 30+ days</li>
                    <li>✓ User level: Minimum Level A (Advanced)</li>
                    <li>✓ Free prompts: 10+ created</li>
                    <li>✓ Ratings given: 25+</li>
                    <li>✓ Ratings received: 15+</li>
                    <li>✓ Average rating: 7.0+</li>
                    <li>✓ Good standing (no violations)</li>
                  </ul>
                </div>
              </Card>

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Pricing Guidelines</h3>
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex justify-between">
                      <span>Simple Prompt:</span>
                      <span className="font-semibold">20-30 BC</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Advanced Prompt:</span>
                      <span className="font-semibold">40-80 BC</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prompt Collection:</span>
                      <span className="font-semibold">60-100 BC</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Custom Agent:</span>
                      <span className="font-semibold">80-120 BC</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-300 dark:border-gray-600 pt-2 mt-2">
                      <span className="font-bold">Maximum:</span>
                      <span className="font-bold text-purple-600 dark:text-purple-400">200 BC ($10)</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </DocumentationSection>

          {/* Earning Coins */}
          <DocumentationSection title="Earning Boop Coins" id="earning-coins">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Contribute to the community and earn Boop Coins for free. Use them for premium features or save up to cash out.
            </p>

            <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Reward
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Quality Rating</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">2 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">50+ character comment</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Helpful Comment</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">5 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">10+ helpful votes</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Weekly Top 10</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">50 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">Make leaderboard</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Badge Achievement</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">10-100 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">Tier-based reward</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Referral</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">25 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">Friend creates 3+ prompts</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">Approved Report</td>
                    <td className="px-6 py-4 text-purple-600 dark:text-purple-400 font-semibold">5 BC</td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">Content moderation</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                <strong>Daily Earning Cap:</strong> Maximum 20 BC per day from ratings and comments to prevent farming.
                Badges, leaderboard, and referrals are not capped.
              </p>
            </div>
          </DocumentationSection>

          {/* Premium Features */}
          <DocumentationSection title="Premium Features" id="premium-features">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Prompt Improver
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 font-semibold mb-3">15 BC per use</p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>• AI-powered structure analysis</li>
                    <li>• Clarity improvement suggestions</li>
                    <li>• Alternative phrasings</li>
                    <li>• Before/after token comparison</li>
                  </ul>
                </div>
              </Card>

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Advanced Token Analysis
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 font-semibold mb-3">10 BC per use</p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>• Model-specific optimization</li>
                    <li>• Cost estimation across models</li>
                    <li>• Suggested token-reducing rewrites</li>
                    <li>• Complexity analysis</li>
                  </ul>
                </div>
              </Card>

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Boost Your Prompt
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 font-semibold mb-3">50 BC per 24hrs</p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>• Featured homepage placement</li>
                    <li>• Category page highlighting</li>
                    <li>• 5x visibility in feeds</li>
                    <li>• "Boosted" badge on card</li>
                  </ul>
                </div>
              </Card>

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Analytics Dashboard
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 font-semibold mb-3">20 BC per month</p>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>• Detailed view statistics</li>
                    <li>• Geographic breakdown</li>
                    <li>• Conversion metrics</li>
                    <li>• Performance comparisons</li>
                  </ul>
                </div>
              </Card>
            </div>
          </DocumentationSection>

          {/* Best Practices */}
          <DocumentationSection title="Best Practices" id="best-practices">
            <div className="space-y-6">
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Rating Guidelines</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span><strong>Test first:</strong> Always try the prompt before rating</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span><strong>Be specific:</strong> Explain what worked or didn't work</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span><strong>Use all dimensions:</strong> Rate Overall, Clarity, Creativity, Effectiveness</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-2">•</span>
                      <span><strong>Be constructive:</strong> Focus on helpfulness, not just criticism</span>
                    </li>
                  </ul>
                </div>
              </Card>

              <Card>
                <div className={`p-${designTokens.spacing.cardPadding}`}>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Community Guidelines</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                      <span>Be respectful and constructive</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                      <span>Share credit when forking or building on others' work</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                      <span>Report violations to keep the community safe</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 dark:text-red-400 mr-2">✗</span>
                      <span>Don't spam, manipulate ratings, or harass others</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 dark:text-red-400 mr-2">✗</span>
                      <span>Don't share inappropriate, illegal, or harmful content</span>
                    </li>
                  </ul>
                </div>
              </Card>
            </div>
          </DocumentationSection>

          {/* Help CTA */}
          <div className="mt-12 bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need More Help?</h2>
            <p className="text-purple-100 mb-6">
              Check out our FAQ or contact our support team
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/faq"
                className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                View FAQ
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Documentation;
